import { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui-kit/toggle-group';
import { TableDensity, useTableDensity } from '@/hooks/use-table-density';

/** Rows per page (Component Map › Pagination). */
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

/** The density switch's icons: tighter rows draw more lines. */
const DENSITY_ICON: Record<TableDensity, { label: string; lines: number }> = {
  condensed: { label: 'CONDENSED_ROWS', lines: 4 },
  regular: { label: 'REGULAR_ROWS', lines: 3 },
  relaxed: { label: 'RELAXED_ROWS', lines: 2 },
};

const DensityIcon = ({ lines }: { lines: number }) => (
  <svg viewBox="0 0 16 16" className="size-[15px]" aria-hidden>
    {Array.from({ length: lines }, (_, index) => {
      const y = 2 + ((12 / (lines - 1)) * index || 0);
      return <line key={y} x1="2" x2="14" y1={y} y2={y} stroke="currentColor" strokeWidth="1.5" />;
    })}
  </svg>
);

/**
 * The row density switch (Component Map › Row density): three icons, remembered in this browser and
 * shared by every table. Used in a table's footer, and by lists that are not a data table (the leave
 * calendar) so they offer the same choice.
 */
export function RowDensitySwitch() {
  const { t } = useTranslation();
  const [density, setDensity] = useTableDensity();
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      size="icon"
      value={density}
      onValueChange={(next) => next && setDensity(next as TableDensity)}
      aria-label={t('ROW_DENSITY')}
    >
      {(Object.keys(DENSITY_ICON) as TableDensity[]).map((value) => (
        <ToggleGroupItem
          key={value}
          value={value}
          aria-label={t(DENSITY_ICON[value].label)}
          title={t(DENSITY_ICON[value].label)}
        >
          <DensityIcon lines={DENSITY_ICON[value].lines} />
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  /** Kept for callers; the footer never shows a selection count (the bulk toolbar does). */
  showSelectedRowContent?: boolean;
  /** Rows across all pages. */
  totalCount?: number;
  /** The row density switch (Component Map › Row density); on by default. */
  showDensity?: boolean;
}

/**
 * A table's footer (HR Module Prototype, Component Map › Pagination and Row density): "1–25 of 132",
 * or "Showing 12 of 12" when everything fits on one page; then the density switch and, only when there
 * are more than 25 rows, the pager: 25 / 50 / 100 rows per page, the page, and first / previous / next /
 * last.
 */
export function DataTablePagination<TData>({
  table,
  onPaginationChange,
  totalCount,
  showDensity = true,
}: Readonly<DataTablePaginationProps<TData>>) {
  const { t } = useTranslation();
  const { pageIndex, pageSize } = table.getState().pagination;
  const total = totalCount ?? table.getFilteredRowModel().rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const isPaged = total > pageSize;
  const from = total ? pageIndex * pageSize + 1 : 0;
  const to = Math.min((pageIndex + 1) * pageSize, total);
  const showPager = total > DEFAULT_PAGE_SIZE || pageSize !== DEFAULT_PAGE_SIZE;

  const goTo = (nextIndex: number) => {
    table.setPageIndex(nextIndex);
    onPaginationChange?.({ pageIndex: nextIndex, pageSize });
  };
  const pageButtons = [
    { label: 'GO_TO_FIRST_PAGE', icon: ChevronsLeft, index: 0 },
    { label: 'GO_TO_PREVIOUS_PAGE', icon: ChevronLeft, index: pageIndex - 1 },
    { label: 'GO_TO_NEXT_PAGE', icon: ChevronRight, index: pageIndex + 1 },
    { label: 'GO_TO_LAST_PAGE', icon: ChevronsRight, index: pageCount - 1 },
  ];

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-3 text-[13px] text-muted-foreground">
      <span className="tabular-nums">
        {isPaged
          ? t('DATA_TABLE_RANGE', { from, to, total })
          : t('DATA_TABLE_SHOWING_COUNT', { count: total, total })}
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-4 max-md:w-full max-md:justify-between">
        {showDensity && <RowDensitySwitch />}
        {showPager && (
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => {
                const nextSize = Number(value);
                table.setPageSize(nextSize);
                onPaginationChange?.({ pageSize: nextSize, pageIndex: 0 });
              }}
            >
              <SelectTrigger className="w-[72px]" aria-label={t('ROWS_PER_PAGE')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {Array.from(new Set<number>([...PAGE_SIZE_OPTIONS, pageSize])).map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="whitespace-nowrap tabular-nums">
              {t('DATA_TABLE_PAGE_OF', { page: pageIndex + 1, pages: pageCount })}
            </span>
            <div className="flex gap-1">
              {pageButtons.map(({ label, icon: Icon, index }) => (
                <Button
                  key={label}
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={t(label)}
                  title={t(label)}
                  disabled={index < 0 || index >= pageCount || index === pageIndex}
                  onClick={() => goTo(index)}
                >
                  <Icon className="size-4" />
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
